/**
 * The geo domain's client-side vocabulary, WP-W10 — small pieces of parsing and
 * presentation that live here rather than in a screen because more than one
 * component reads them:
 *
 *  - `geoCheckinConflictReason` reads the `409 WORKFORCE_CONFLICT` `recordGeoCheckin`
 *    raises back into a named reason, the same "print the server's own extra detail
 *    rather than re-derive it" instinct `hr/leave.ts`'s `insufficientBalanceDetails`
 *    applies to the leave over-draw refusal. `WORKFORCE_CONFLICT` is on
 *    `error-safety.test.ts`'s user-safe list, so `ApiErrorNote` already prints the
 *    server's own sentence for both reasons below — this exists so a screen (or a
 *    test) can branch on *which* refusal this is without pattern-matching prose.
 *  - `geoCheckinOutsideGeofenceDetail` does the same for the `400
 *    WORKFORCE_GEO_OUTSIDE_GEOFENCE` refusal `block` mode raises (Task T2-C-web):
 *    `details.distance_m` / `details.radius_m`, read off the refusal itself rather
 *    than guessed, because an employee cannot read the company's policy
 *    (`workforce.manage_policies`-gated) to know its threshold — the refusal body is
 *    the only place they can learn how far they are from the geofence.
 *  - `geolocationFailureKind` names the four `GeolocationPositionError.code` values
 *    the **browser** produces — never the server, so it cannot appear in
 *    `api/errors.ts` — because a screen branching on a bare `1`/`2`/`3` is a screen
 *    nobody can read six months on.
 *  - `browserGeolocation` reads `navigator.geolocation` narrowed to `unknown` first,
 *    the same technique `devices/reveal.ts`'s `browserClipboard` uses for
 *    `navigator.clipboard`: TypeScript's DOM lib declares `Navigator.geolocation` as
 *    **non-optional**, which is a lie in exactly the case that matters — jsdom ships
 *    no Geolocation API at all, and a browser may withhold it in a non-secure
 *    context. A guard on a type the compiler believes cannot be falsy is a guard
 *    `@typescript-eslint/no-unnecessary-condition` deletes, taking the real branch
 *    with it.
 *  - `OFFICE_STATUS_PRESENTATION` / `officeStatusOf` are `people/status.ts`'s own
 *    pattern, transcribed for an office geofence's `is_active` flag: DESIGN.md §19
 *    forbids colour alone, so a badge needs an icon *and* a label, and both come
 *    from the presentation entry itself — never `SEVERITY_ICON[severity]` computed
 *    at the call site, which is the exact regression T2-B's fix round 1 found (two
 *    status badges silently rendering the wrong glyph because the icon was
 *    re-derived from the severity instead of read off the row's own entry).
 */

import type { WorkforceApiError } from '../api/errors';
import type { MessageKey } from '../i18n/messages';
import type { IconName, Severity } from '../states/icons';

/** `GeoCheckinRequest`'s two `409 WORKFORCE_CONFLICT` `details.reason` values. */
export type GeoCheckinConflictReason = 'geo_checkin_disabled' | 'geo_consent_required';

export function geoCheckinConflictReason(
  error: WorkforceApiError,
): GeoCheckinConflictReason | null {
  if (error.code !== 'WORKFORCE_CONFLICT') return null;
  const { reason } = error.details;
  return reason === 'geo_checkin_disabled' || reason === 'geo_consent_required' ? reason : null;
}

/**
 * `distance_m` / `radius_m` from a `400 WORKFORCE_GEO_OUTSIDE_GEOFENCE` refusal
 * (`recordGeoCheckin`, `block` mode). `null` for anything else — a different code, a
 * malformed `details` — so a screen can render its own sentence only when both
 * numbers are actually there rather than interpolating `undefined` into it.
 */
export interface GeoOutsideGeofenceDetail {
  readonly distanceM: number;
  readonly radiusM: number;
}

export function geoCheckinOutsideGeofenceDetail(
  error: WorkforceApiError,
): GeoOutsideGeofenceDetail | null {
  if (error.code !== 'WORKFORCE_GEO_OUTSIDE_GEOFENCE') return null;
  const { distance_m: distanceM, radius_m: radiusM } = error.details;
  if (typeof distanceM !== 'number' || typeof radiusM !== 'number') return null;
  return { distanceM, radiusM };
}

/** An office geofence's `is_active` flag, as a display status. */
export type OfficeDisplayStatus = 'active' | 'inactive';

export interface OfficeStatusPresentation {
  readonly severity: Severity;
  readonly icon: IconName;
  readonly labelKey: MessageKey;
}

/**
 * Active reads `low` — a good, unremarkable fact, the same severity
 * `people/status.ts` gives its own `active` row. Inactive reads `info`: an
 * administrator turned it off on purpose, not a problem to flag louder, and `ban`
 * is a silhouette distinct from `circle-check` at badge size (DESIGN.md §4).
 */
export const OFFICE_STATUS_PRESENTATION: Readonly<
  Record<OfficeDisplayStatus, OfficeStatusPresentation>
> = {
  active: { severity: 'low', icon: 'circle-check', labelKey: 'geo.offices.active' },
  inactive: { severity: 'info', icon: 'ban', labelKey: 'geo.offices.inactive' },
};

export function officeStatusOf(office: { readonly is_active: boolean }): OfficeDisplayStatus {
  return office.is_active ? 'active' : 'inactive';
}

/**
 * The four cases `navigator.geolocation.getCurrentPosition` can leave a check-in in.
 * `'unsupported'` is not one of the DOM's own `GeolocationPositionError` codes — it is
 * this client's own fact, for a browser or context (e.g. plain `http://`) that has no
 * `navigator.geolocation` at all, told apart because there is nothing to retry there.
 */
export type GeolocationFailureKind =
  'permission_denied' | 'position_unavailable' | 'timeout' | 'unsupported';

const CODE_TO_KIND: Readonly<Record<number, GeolocationFailureKind>> = {
  1: 'permission_denied',
  2: 'position_unavailable',
  3: 'timeout',
};

/** `GeolocationPositionError.code` (1/2/3), named. Unknown codes fall back to `position_unavailable`. */
export function geolocationFailureKind(error: GeolocationPositionError): GeolocationFailureKind {
  return CODE_TO_KIND[error.code] ?? 'position_unavailable';
}

/**
 * `navigator.geolocation`, if this document actually has one.
 *
 * Read as `unknown` and narrowed, rather than trusted and then guarded — see this
 * file's own header for why. Callers get back only the one method this screen calls,
 * never the whole `Geolocation` interface, so a test can supply a minimal fake
 * (`{ getCurrentPosition: vi.fn() }`) without shape-checking against the rest of it.
 */
export function browserGeolocation(): Pick<Geolocation, 'getCurrentPosition'> | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const candidate: unknown = (navigator as { geolocation?: unknown }).geolocation;
  if (candidate === null || typeof candidate !== 'object') return undefined;
  const getCurrentPosition: unknown = (candidate as { getCurrentPosition?: unknown })
    .getCurrentPosition;
  return typeof getCurrentPosition === 'function'
    ? (candidate as Pick<Geolocation, 'getCurrentPosition'>)
    : undefined;
}
